import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Hotel, Users, Bed, Calendar, DollarSign, Mail, Phone, MapPin, Pencil, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import PhoneInput, { formatPhoneNumber, parsePhoneNumber } from '@/components/PhoneInput';
import AddressInput, { formatAddress } from '@/components/AddressInput';

export default function HotelDetails({ user: userProp, isTab, tabId, tabData }) {
  const { hotelId: routeHotelId } = useParams();
  const navigate = useNavigate();
  
  // Get user from prop or localStorage (for tab-based rendering)
  const [user] = useState(() => userProp || JSON.parse(localStorage.getItem('user') || '{}'));
  
  // Get hotelId from route params or tabData (for tab-based rendering)
  const hotelId = routeHotelId || tabData?.hotelId;
  
  const [hotel, setHotel] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_id: '',
    address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pin_code: '',
      country: 'India'
    }
  });

  useEffect(() => {
    loadHotelDetails();
    loadCompanies();
  }, [hotelId]);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies');
    }
  };

  const loadHotelDetails = async () => {
    try {
      const response = await api.get(`/super-admin/hotels/${hotelId}`);
      setHotel(response.data);
    } catch (error) {
      toast.error('Failed to load hotel details');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    try {
      await api.post(`/super-admin/hotels/${hotelId}/suspend`);
      toast.success('Hotel suspended');
      loadHotelDetails();
    } catch (error) {
      toast.error('Failed to suspend hotel');
    }
  };

  const handleActivate = async () => {
    try {
      await api.post(`/super-admin/hotels/${hotelId}/activate`);
      toast.success('Hotel activated');
      loadHotelDetails();
    } catch (error) {
      toast.error('Failed to activate hotel');
    }
  };

  const handleEditClick = () => {
    setEditFormData({
      name: hotel.name || '',
      email: hotel.email || '',
      phone: hotel.phone || '',
      company_id: hotel.company_id || '',
      address: {
        address_line1: hotel.address || '',
        address_line2: '',
        city: hotel.city || '',
        state: hotel.state || '',
        pin_code: hotel.pin_code || '',
        country: hotel.country || 'India'
      }
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // Flatten address for API
      const payload = {
        name: editFormData.name,
        email: editFormData.email,
        phone: editFormData.phone,
        company_id: editFormData.company_id || null,
        address: editFormData.address?.address_line1 || '',
        city: editFormData.address?.city || '',
        state: editFormData.address?.state || '',
        country: editFormData.address?.country || 'India',
        pin_code: editFormData.address?.pin_code || ''
      };
      
      await api.patch(`/super-admin/hotels/${hotelId}`, payload);
      toast.success('Hotel updated successfully');
      setEditDialogOpen(false);
      loadHotelDetails();
    } catch (error) {
      toast.error('Failed to update hotel');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'trial': return 'bg-blue-100 text-blue-700';
      case 'suspended': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return <div className="text-center py-12" data-testid="hotel-details-loading">Loading hotel details...</div>;
  }

  if (!hotel) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Hotel not found</p>
        <Button onClick={() => navigate('/admin/super-admin/dashboard')} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hotel-details-page">
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/super-admin/dashboard')}
          className="mb-4"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
              {hotel.name}
            </h1>
            <p className="text-muted-foreground mt-1">Hotel Details & Management</p>
          </div>
          <Badge className={getStatusColor(hotel.status)} data-testid="hotel-status-badge">
            {hotel.status}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hotel Information */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Hotel Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {/* Company */}
                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Company</p>
                    <p className="font-medium">
                      {companies.find(c => c.id === hotel.company_id)?.name || 
                       hotel.company_name || 
                       <span className="text-muted-foreground italic">Not assigned</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Email</p>
                    <p className="font-medium">{hotel.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Phone</p>
                    <p className="font-medium">{hotel.phone}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Address</p>
                    <p className="font-medium">{hotel.address}</p>
                    <p className="text-sm text-muted-foreground">
                      {hotel.city}, {hotel.state}
                    </p>
                    <p className="text-sm text-muted-foreground">{hotel.country}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h3 className="font-semibold mb-3">Subscription Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Status</p>
                  <Badge className={getStatusColor(hotel.status)}>{hotel.status}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Created</p>
                  <p className="font-medium">{new Date(hotel.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Max Users</p>
                  <p className="font-medium">{hotel.max_users || 50}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Max Rooms</p>
                  <p className="font-medium">{hotel.max_rooms || 100}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics */}
        <Card>
          <CardHeader>
            <CardTitle>Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="font-medium">Total Users</span>
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {hotel.stats?.total_users || 0}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Bed className="h-5 w-5 text-green-600" />
                  <span className="font-medium">Total Rooms</span>
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {hotel.stats?.total_rooms || 0}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-purple-600" />
                  <span className="font-medium">Total Guests</span>
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {hotel.stats?.total_guests || 0}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-amber-600" />
                  <span className="font-medium">Total Bookings</span>
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {hotel.stats?.total_bookings || 0}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-emerald-600" />
                  <span className="font-medium">Active Bookings</span>
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {hotel.stats?.active_bookings || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Hotel Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={handleEditClick}
              data-testid="edit-hotel-button"
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit Hotel
            </Button>
            {hotel.status === 'active' ? (
              <Button
                variant="destructive"
                onClick={handleSuspend}
                data-testid="suspend-hotel-button"
              >
                Suspend Hotel
              </Button>
            ) : hotel.status === 'suspended' ? (
              <Button
                onClick={handleActivate}
                data-testid="activate-hotel-button"
              >
                Activate Hotel
              </Button>
            ) : hotel.status === 'trial' ? (
              <Button
                onClick={handleActivate}
                data-testid="activate-trial-hotel-button"
              >
                Activate Hotel
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={() => navigate('/admin/super-admin/dashboard')}
            >
              Back to All Hotels
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Edit Hotel Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md" data-testid="edit-hotel-dialog">
          <DialogHeader>
            <DialogTitle>Edit Hotel</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Hotel Name</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
                data-testid="edit-hotel-name"
              />
            </div>
            
            {/* Company Selection */}
            <div className="space-y-2">
              <Label htmlFor="edit-company">Company</Label>
              <Select 
                value={editFormData.company_id || "none"} 
                onValueChange={(value) => setEditFormData({ ...editFormData, company_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="edit-hotel-company">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Company</SelectItem>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-500" />
                        <span>{company.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
                data-testid="edit-hotel-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <PhoneInput
                value={editFormData.phone}
                onChange={(value) => setEditFormData({ ...editFormData, phone: value })}
                data-testid="edit-hotel-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <AddressInput
                value={editFormData.address}
                onChange={(value) => setEditFormData({ ...editFormData, address: value })}
                data-testid="edit-hotel-address"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1" data-testid="save-hotel-edit">
                Save Changes
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
